import { useState } from "react";

import Card from "#/components/shared/card";
import ThemedSpinner from "#/components/shared/spinner";
import Typography from "#/components/shared/typography";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import { useLoadContractRequests } from "#/hooks/useLoadContractRequests";
import MedicalRecordsRequestList from "./MedicalRecordsRequestList";
import StartRequestModal from "./StartRequestModal";

interface MedicalRecordsRequestsSectionProps {
  userId: string;
}

export function MedicalRecordsRequestsSection({
  userId,
}: MedicalRecordsRequestsSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    requests,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useLoadContractRequests({ userId, enabled: Boolean(userId) });

  const isBusy = isLoading || (!isError && isFetching);

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Typography variant="h2" className="text-terracotta">
            Medical Records Requests
          </Typography>
          <Typography variant="body1" className="text-brown-700">
            Track every authorization you’ve sent and see when it’s ready.
          </Typography>
        </div>
        <div className="flex flex-wrap gap-3">
          <SubmitButton
            type="button"
            color="terracotta"
            onClick={() => setIsModalOpen(true)}
            hideArrow
          >
            Start new request
          </SubmitButton>
          <SubmitButton
            type="button"
            color="cream"
            hideArrow
            onClick={() => refetch()}
            isDisabled={isBusy}
            isLoading={isFetching}
          >
            Refresh
          </SubmitButton>
        </div>
      </div>

      <Card variant="cream" elevation={1}>
        <Card.Body>
          {isBusy ? (
            <div className="flex justify-center py-8">
              <ThemedSpinner />
            </div>
          ) : isError ? (
            <div className="space-y-2">
              <Typography variant="h4" className="text-terracotta">
                {"We couldn’t load your requests"}
              </Typography>
              <Typography variant="body2" className="text-brown-700">
                {error instanceof Error ? error.message : "Please try again later."}
              </Typography>
            </div>
          ) : requests.length === 0 ? (
            <div className="space-y-2 text-center">
              <Typography variant="h4" className="text-terracotta">
                No requests yet
              </Typography>
              <Typography variant="body2" className="text-brown-700">
                Start a request to send a HIPAA release to your provider.
              </Typography>
            </div>
          ) : (
            <MedicalRecordsRequestList items={requests} />
          )}
        </Card.Body>
      </Card>

      <StartRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
}

export default MedicalRecordsRequestsSection;
